import { useState, useEffect, useCallback } from 'react';

interface ArticleDraft {
  prompt: string;
  provider: string;
  wordCount: number;
  tone: string;
  selectedTopic?: {
    title: string;
    description: string;
  };
  generatedContent?: string;
  includeKeyTakeaways: string;
  includeCallToAction: string;
  includeSources: string;
  includeRiskWarnings: string;
  includeTableOfContents: string;
  timestamp: number;
}

const DRAFT_STORAGE_KEY = 'ai-article-draft';
const DRAFT_EXPIRY_DAYS = 7;

export const useArticleDraft = () => {
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    checkForDraft();
  }, []);

  const checkForDraft = () => {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (saved) {
      try {
        const draft: ArticleDraft = JSON.parse(saved);
        const daysSinceCreated = (Date.now() - draft.timestamp) / (1000 * 60 * 60 * 24);
        
        if (daysSinceCreated < DRAFT_EXPIRY_DAYS) {
          setHasDraft(true);
        } else {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          setHasDraft(false);
        }
      } catch (error) {
        console.error('Error reading draft:', error);
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        setHasDraft(false);
      }
    }
  };

  const saveDraft = useCallback((draft: Omit<ArticleDraft, 'timestamp'>) => {
    try {
      const draftWithTimestamp: ArticleDraft = {
        ...draft,
        timestamp: Date.now()
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftWithTimestamp));
      setHasDraft(true);
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, []);

  const loadDraft = useCallback((): ArticleDraft | null => {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!saved) return null;

    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
  }, []);

  return {
    hasDraft,
    saveDraft,
    loadDraft,
    clearDraft
  };
};
